import React from 'react';
import { makeStyles, createStyles, Paper, Typography, Theme } from '@material-ui/core';
import * as MuiIcons from '@mui/icons-material';
import { lighten } from '@material-ui/core/styles';
import { SvgIconProps } from '@material-ui/core/SvgIcon';
import DOMPurify from 'dompurify';

interface PresetCardProps {
  title: string;
  description: string;
  iconName: string;
  color: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      padding: theme.spacing(2),
      borderRadius: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      alignItems: 'flex-start',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shorter,
      }),
      '&:hover': {
        transform: 'translateY(-3px)',
      },
    },
    icon: {
      marginRight: theme.spacing(2),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 64,
      height: 64,
      borderRadius: theme.spacing(2),
      color: (props: { color: string }) => props.color || theme.palette.primary.main,
      backgroundColor: (props: { color: string }) => {
        try {
          return props.color ? lighten(props.color, 0.9) : lighten(theme.palette.primary.main, 0.9);
        } catch (error) {
          return lighten(theme.palette.primary.main, 0.9);
        }
      },
    },
    iconSvg: {
      fontSize: theme.typography.pxToRem(36),
    },
    contentContainer: {
      flex: 1,
    },
    title: {
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(0.5),
    },
    description: {
      color: theme.palette.text.secondary,
      fontSize: theme.typography.pxToRem(14),
    },
  })
);

const PresetCard: React.FC<PresetCardProps> = ({ title, description, iconName, color }) => {
  const classes = useStyles({ color });

  const IconComponent =
    (MuiIcons as unknown as Record<string, React.ComponentType<SvgIconProps>>)[iconName] || null;

  const allowedTags = ['b', 'i', 'em', 'strong', 'a', 'br'];
  const sanitize = (html: string) =>
    DOMPurify.sanitize(html, {
      ALLOWED_TAGS: allowedTags,
      ALLOWED_ATTR: ['href', 'target', 'rel'],
    });

  return (
    <Paper className={classes.card} elevation={0}>
      <div className={classes.icon}>
        {IconComponent ? <IconComponent className={classes.iconSvg} /> : iconName}
      </div>
      <div className={classes.contentContainer}>
        <Typography className={classes.title} variant="subtitle1" component="div">
          {/* eslint-disable-next-line react/no-danger */}
          <span dangerouslySetInnerHTML={{ __html: sanitize(title) }} />
        </Typography>
        <Typography className={classes.description} variant="body2" component="div">
          {/* eslint-disable-next-line react/no-danger */}
          <span dangerouslySetInnerHTML={{ __html: sanitize(description) }} />
        </Typography>
      </div>
    </Paper>
  );
};

export default PresetCard;
